import { set_token, get_token, set_user_details, getCookie, csrf_headers } from "@/router";
import Swal from "sweetalert2";

const PASSWORD_RESET_URL = process.env.VUE_APP_RESET_URL;
import { client_domain, client_features } from "@/main.js";

export default {
    data() {
        return ({
            email: '',
            password: '',
            passwordVisible: false,
            reset_url: "",
            cbo_domain: false,
            dwypd_domain: false,
            ict_chamber: false,
            vettedsa_domain: false,
            bosa_domain: false,
            isSubmitting: false,
            workbook_domain: false,
            csrfToken: '',
        });
    },
    mounted() {
        this.reset_url = PASSWORD_RESET_URL;
        if (client_domain.includes('cbo')) this.cbo_domain = true;
        if (client_domain.includes('dwypd')) this.dwypd_domain = true;
        if (client_domain.includes('chamber')) this.ict_chamber = true;
        if (client_domain.includes('vettedsa')) this.vettedsa_domain = true;
        if (this.is_bosa_domain()) this.bosa_domain = true;
        if (client_domain.includes('workbook')) this.workbook_domain = true;
        this.csrfToken = getCookie('csrftoken');
    },
    methods: {
        togglePasswordVisibility() {
            this.passwordVisible = !this.passwordVisible;
        },

        async ensure_csrf_cookie() {
            const csrfUrl = `${process.env.VUE_APP_BACKEND_API_URL}/system_management/get_csrf_token/`;
            try {
                await fetch(csrfUrl, {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Page-URL': window.location.href,
                    },
                });
            } catch (e) {
                console.warn('Failed to refresh CSRF cookie', e);
            } finally {
                this.csrfToken = getCookie('csrftoken') || this.csrfToken;
            }
        },

        async handleSubmit() {
            this.isSubmitting = true;
            this.clear_profile_modal_session();
            await this.ensure_csrf_cookie();

            $.ajax({
                type: "POST",
                url: `${process.env.VUE_APP_BACKEND_API_URL}/system_management/login/`,
                contentType: "application/json",
                xhrFields: { withCredentials: true },
                headers: {
                    'Content-Type': 'application/json',
                    ...csrf_headers(this.csrfToken),
                },
                data: JSON.stringify({
                    email: this.email.toLowerCase(),
                    password: this.password
                }),
                success: async (response) => {
                    const status = response.status;

                    if (status === "success") {
                        Swal.fire({
                            icon: 'success',
                            title: 'Login Successful',
                            showConfirmButton: false,
                            timer: 1000,
                            position: 'bottom-end',
                            toast: true
                        }).then(async () => {
                            const reponse_data = response.data;

                            set_token(reponse_data.token);
                            set_user_details(reponse_data.user_details);

                            if (window.flutter_inappwebview) {
                                window.flutter_inappwebview.callHandler("authToken", response.token);
                            }

                            let next_url = reponse_data.redirect_url;

                            if (this.is_bosa_domain()) {
                                next_url = await this.get_bosa_next_url(reponse_data.user_details);
                            } else if (this.has_missing_required_profile_details(reponse_data.user_details)) {
                                next_url = this.get_profile_name_update_url(reponse_data.user_details);
                            } else if (this.is_first_time_login(reponse_data.user_details) && client_features.marketplace) {
                                next_url = '/marketplace';
                            } else if (this.cbo_domain) {
                                next_url = '/view_events';
                            } else if (this.ict_chamber) {
                                // Wait for membership check
                                next_url = await this.get_membership_next_url();
                            } else if (this.dwypd_domain) {
                                next_url = '/knowledgehub';
                            } else if (this.vettedsa_domain) {
                                next_url = '/marketplace';
                            } else {
                                next_url = sessionStorage.getItem('next') || next_url;
                            }

                            window.location = next_url;
                        });
                    } else if (status === "created_user") {
                        set_token(response.token);
                        set_user_details(response.user_details);
                        if (this.is_bosa_domain()) {
                            window.location = await this.get_bosa_next_url(response.user_details);
                        } else if (this.has_missing_required_profile_details(response.user_details)) {
                            window.location = this.get_profile_name_update_url(response.user_details);
                        } else if (client_features.marketplace) {
                            window.location = '/marketplace';
                        } else {
                            window.location = '/home';
                        }
                    }
                },
                error: (xhr) => {
                    if (xhr?.status === 403) {
                        Swal.fire({
                            icon: 'error',
                            title: 'Forbidden (403)',
                            text: 'CSRF token missing/invalid or cookies not included. Please refresh and try again.',
                            position: 'bottom-end',
                            showConfirmButton: false,
                            timer: 3500,
                            toast: true
                        });
                        return;
                    }
                    const response = xhr.responseJSON;
                    if (response && response.status === "unverified") {
                        Swal.fire({
                            icon: 'warning',
                            title: 'Email Not Verified',
                            text: 'Please check your inbox and verify your email before logging in.',
                            position: 'bottom-end',
                            showConfirmButton: false,
                            timer: 3000,
                            toast: true
                        });
                    } else {
                        Swal.fire({
                            icon: 'error',
                            title: 'Incorrect Email or Password',
                            position: 'bottom-end',
                            showConfirmButton: false,
                            timer: 3000,
                            toast: true
                        });
                    }
                },
                complete: () => {
                    this.isSubmitting = false;
                }
            });
        },

        is_first_time_login(user_details) {
            const first_time_login = user_details?.first_time_login;
            return first_time_login === true || first_time_login === 'true';
        },

        has_missing_names(user_details) {
            return !user_details?.first_name?.trim() || !user_details?.last_name?.trim();
        },

        has_missing_required_profile_details(user_details) {
            return this.has_missing_names(user_details)
                || this.has_missing_profile_field(user_details, ['race', 'race_id'])
                || this.has_missing_profile_field(user_details, ['gender', 'gender_id'])
                || this.has_missing_profile_field(user_details, ['age', 'date_of_birth'])
                || this.has_missing_profile_field(user_details, ['location']);
        },

        has_missing_profile_field(user_details, field_names) {
            const profile = user_details?.profile || {};
            return !field_names.some((field_name) => this.has_profile_value(this.get_allowed_profile_field_value(user_details, field_name))
                || this.has_profile_value(this.get_allowed_profile_field_value(profile, field_name)));
        },

        get_allowed_profile_field_value(source, field_name) {
            if (!source) return undefined;

            const allowed_profile_fields = [
                ['race', source.race],
                ['race_id', source.race_id],
                ['gender', source.gender],
                ['gender_id', source.gender_id],
                ['age', source.age],
                ['date_of_birth', source.date_of_birth],
                ['location', source.location],
            ];
            const matched_field = allowed_profile_fields.find(([allowed_field_name]) => allowed_field_name === field_name);
            return matched_field ? matched_field[1] : undefined;
        },

        has_profile_value(value) {
            if (value === null || value === undefined) return false;
            return typeof value === 'string' ? value.trim() !== '' : true;
        },

        get_client_domain_value() {
            return String(client_domain
                || document.documentElement.getAttribute('data-client')
                || window.location.hostname
                || '').toLowerCase();
        },

        is_bosa_domain() {
            return this.get_client_domain_value().includes('bosa');
        },

        async get_bosa_next_url(user_details) {
            const profile_data = await this.get_logged_in_user_profile(user_details);
            const required_details = this.build_profile_required_details(user_details, profile_data);

            if (this.has_missing_required_profile_details(required_details)) {
                return this.get_profile_name_update_url(user_details);
            }

            return '/home';
        },

        get_logged_in_user_profile(user_details) {
            return new Promise((resolve) => {
                $.ajax({
                    type: "GET",
                    url: `${process.env.VUE_APP_BACKEND_API_URL}/system_management/get_profile/`,
                    headers: {
                        Authorization: `Token ${get_token()}`,
                        'Content-Type': 'application/json',
                    },
                    data: { user_id: user_details?.id || user_details?.user_id || '' },
                    success: (response) => {
                        resolve(response?.data?.profile_data || null);
                    },
                    error: () => {
                        resolve(null);
                    }
                });
            });
        },

        build_profile_required_details(user_details, profile_data) {
            if (!profile_data) {
                return user_details;
            }

            return ({
                ...user_details,
                profile: {
                    ...user_details?.profile,
                    race: profile_data.race,
                    gender: profile_data.gender,
                    date_of_birth: profile_data.date_of_birth,
                    location: profile_data.location,
                }
            });
        },

        get_profile_name_update_url(user_details) {
            const user_id = user_details?.id || user_details?.user_id || '';
            sessionStorage.setItem('open_profile_modal', 'true');
            if (user_id) {
                sessionStorage.setItem('profile_target_user_id', user_id);
            } else {
                sessionStorage.removeItem('profile_target_user_id');
            }
            return '/profile';
        },

        clear_profile_modal_session() {
            sessionStorage.removeItem('open_profile_modal');
            sessionStorage.removeItem('profile_target_user_id');
            sessionStorage.removeItem('profile_target_user_name');
        },

        get_membership_next_url() {
            return new Promise((resolve) => {
                $.ajax({
                    type: "GET",
                    url: `${process.env.VUE_APP_BACKEND_API_URL}/membership_management/check_user_membership/`,
                    headers: {
                        Authorization: `Token ${get_token()}`,
                        'Content-Type': 'application/json',
                    },
                    success: (response) => {
                        let next_url = response.has_membership ? '/home' : '/view_membership';
                        resolve(next_url);
                    },
                    error: () => {
                        resolve('/home'); // fallback
                    }
                });
            });
        }
    }
};
